import { Listing, OfferType } from '@smg-real-estate/hg-rets-schema';
import { AgencyAgent, ListerBranding } from '@smg-real-estate/hg-search-libs';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import {
  AgencyCardOutcome as ACO,
  getAgencyCardOutcome,
} from '@/helpers/AgencyCard/getAgencyCardOutcome';
import { getPartnerBadge } from '@/helpers/partnerBadge/getPartnerBadge';

export interface UseAgencyCardOutcomeInput {
  listing: MaybeRefOrGetter<Listing | undefined>;
  listerBranding: MaybeRefOrGetter<ListerBranding | null | undefined>;
  agencyAgent: MaybeRefOrGetter<AgencyAgent | null | undefined>;
}

export function useAgencyCardOutcome({
  listing,
  listerBranding,
  agencyAgent,
}: UseAgencyCardOutcomeInput) {
  const outcome = computed(() =>
    getAgencyCardOutcome({
      isPremiumBranding: !!toValue(listerBranding)?.isPremiumBranding,
      hasAgencyAgent: !!toValue(agencyAgent),
      basePackage: toValue(listerBranding)?.basePackage,
      offerType: toValue(listing)?.offerType as OfferType | undefined,
    }),
  );
  const isOutcome = (...outcomes: ACO[]) => outcomes.includes(outcome.value);
  const shouldShowAgencyCard = computed(() =>
    isOutcome(ACO.AgencyCardWithPhoneWithoutBadge, ACO.AgencyCardWithoutPhoneWithBadge),
  );
  const shouldShowBrandedListingBox = computed(() =>
    isOutcome(ACO.BrandedListingBoxWithBadge, ACO.BrandedListingBoxWithoutBadge),
  );
  const shouldShowPhoneNumber = computed(() => isOutcome(ACO.AgencyCardWithPhoneWithoutBadge));
  const shouldShowPartnerBadge = computed(() =>
    isOutcome(ACO.AgencyCardWithoutPhoneWithBadge, ACO.BrandedListingBoxWithBadge),
  );
  const partnerBadge = computed(() =>
    shouldShowPartnerBadge.value ? getPartnerBadge(toValue(listerBranding)?.basePackage) : null,
  );

  return {
    outcome,
    shouldShowAgencyCard,
    shouldShowBrandedListingBox,
    shouldShowPhoneNumber,
    partnerBadge,
  };
}
